import { unByKey } from 'ol/Observable';
import { createEditingStyle } from 'ol/style/Style';

import { DrawInteraction } from 'cadenza/map/interactions/draw-interaction';
import { cloneStyle } from 'cadenza/map/style/style-util';
import type { Map } from 'cadenza/map/map';
import { GeometryType } from 'cadenza/map/geom/geometry-type';

import type VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';
import type { EventsKey } from 'ol/events';
import type { StyleLike } from 'ol/style/Style';
import type { Geometry } from 'ol/geom';
import type { GeometryFunction } from 'ol/interaction/Draw';

interface SketchDrawOptions {
  map: Map;
  source: VectorSource<Geometry>;
  type: GeometryType;
  featureStyle: StyleLike;
  isTextModeDrawing?: boolean;
  style?: StyleLike;
  geometryFunction?: GeometryFunction;
}

export class SketchDrawInteraction extends DrawInteraction {

  _sketchDrawEventKeys!: EventsKey[];
  _featureStyle;

  constructor ({ map, featureStyle, ...otherDrawOptions }: SketchDrawOptions) {
    // The default style shows only a point at the mouse cursor and no lines. Then on drawstart we set the featureStyle
    const style = createEditingStyle()[GeometryType.POINT];
    super({ style, featureStyle, ...otherDrawOptions }, map); // featureStyle is needed for text items
    this._featureStyle = featureStyle;
    this.on('change:active', event => {
      if (!event.oldValue) {
        this.onActivation();
      } else {
        this.onDeactivation();
      }
    });
  }

  protected onActivation () {
    this._sketchDrawEventKeys = [
      this.on('drawstart', ({ feature }) => this.onDrawStart(feature)),
      this.on('drawabort', ({ feature }) => this.onDrawAbort(feature)),
      this.on('drawend', ({ feature }) => this.onDrawEnd(feature))
    ];
  }

  protected onDeactivation () {
    unByKey(this._sketchDrawEventKeys);
  }

  protected onDrawStart (feature: Feature) {
    feature.setStyle(cloneStyle(this._featureStyle)); // Otherwise the default layer style is used, see renderer/canvas/VectorLayer::prepareFrame
  }

  protected onDrawAbort (feature: Feature) {
  }

  protected onDrawEnd (feature: Feature) {
  }

  protected finishTextDraw (text: string) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: in draw-interaction.js we also access this private prop
    const sketchFeature = this.sketchFeature_;
    if (sketchFeature) {
      sketchFeature.getStyle().getText().setText(text);
      this.finishDrawing();
    }
  }

}

